import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import BusinessStatus from "@/components/BusinessStatus";
import NotificationBell from "@/components/NotificationBell";
import { useTheme } from "@/context/ThemeContext";
import { useEmployer } from "@/context/EmployerContext";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { to: "/", key: "nav.home", fallback: "Home" },
  { to: "/about", key: "nav.about", fallback: "About" },
  { to: "/services", key: "nav.services", fallback: "Services" },
  { to: "/countries", key: "nav.countries", fallback: "Destinations" },
  { to: "/catalog", key: "nav.catalog", fallback: "Candidates" },
  { to: "/pipeline", key: "nav.pipeline", fallback: "In Process" },
  { to: "/join", key: "nav.join", fallback: "Join Us" },
  { to: "/contact", key: "nav.contact", fallback: "Contact" },
];

// Custom pages built via the Admin Page Builder — appended to the navbar
// whenever the admin marks a page with `in_nav: true`. Cached for 60s so a
// re-render across pages doesn't hammer the API.
function useNavCustomPages() {
  return useQuery({
    queryKey: ["public-pages"],
    queryFn: async () => (await api.get("/pages")).data,
    staleTime: 60_000,
  });
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { settings: brand } = useTheme();
  const { employer } = useEmployer() || {};
  const { user: adminUser } = useAuth() || {};
  const showBell = !!(employer || adminUser);
  const logoUrl = brand?.logo_url
    ? (brand.logo_url.startsWith("http") ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL || ""}${brand.logo_url}`)
    : null;

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const { data: customPages = [] } = useNavCustomPages();
  const navItems = React.useMemo(() => {
    const extras = (customPages || [])
      .filter((p) => p.in_nav)
      .map((p) => ({ to: `/p/${p.slug}`, key: `page.${p.slug}`, fallback: p.title }));
    return [...NAV_ITEMS, ...extras];
  }, [customPages]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/80 border-b border-zinc-200/70" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          {logoUrl ? (
            <img src={logoUrl} alt={settings?.company_name || "Adelina"} className="w-11 h-11 object-contain" data-testid="header-logo-img" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-zinc-950 flex items-center justify-center" style={{borderColor: "var(--brand-primary, #b8860b)", borderWidth: 1, borderStyle: "solid"}}>
              <span className="font-serif text-xl" style={{color: "var(--brand-primary, #b8860b)"}}>A</span>
            </div>
          )}
          <div className="leading-tight">
            <div className="font-serif text-xl tracking-tight text-zinc-950">
              {settings?.company_name || brand?.company_name || "Adelina"}
            </div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-800">{t("brand.tagline", "Employment Agency")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-[#b8860b]" : "text-zinc-950 hover:text-zinc-950"
                }`
              }
              data-testid={`nav-${(it.fallback || "").toLowerCase()}`}
            >
              {t(it.key, it.fallback)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <BusinessStatus size="sm" variant="light" />
          {showBell && <NotificationBell variant="light" />}
          {employer && (
            <Link to="/employer/bookings" className="text-sm text-zinc-800 hover:text-[#b8860b] transition-colors font-medium" data-testid="header-my-bookings-link">
              {t("nav.my_bookings", "My Bookings")}
            </Link>
          )}
          <Link to="/portal" className="text-sm text-zinc-800 hover:text-[#b8860b] transition-colors font-medium" data-testid="header-portal-link">
            {t("nav.portal", "Portal")}
          </Link>
          <Link to="/join" className="btn-gold text-sm" data-testid="header-cta-join">
            {t("action.register_candidate", "Register as Candidate")}
          </Link>
        </div>

        <button
          className="lg:hidden p-2 rounded-full hover:bg-zinc-100"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-zinc-200" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            <BusinessStatus size="md" variant="light" showSub className="self-start" />
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#b8860b]" : "text-zinc-800"}`
                }
                data-testid={`mobile-nav-${(it.fallback || "").toLowerCase()}`}
              >
                {t(it.key, it.fallback)}
              </NavLink>
            ))}
            <Link to="/portal" className="text-sm text-zinc-800 hover:text-[#b8860b] font-medium" data-testid="mobile-portal-link">
              {t("nav.portal", "Portal")}
            </Link>
            <Link to="/catalog" className="btn-gold text-sm inline-block w-max" data-testid="mobile-cta-catalog">
              {t("action.view_profile", "View Candidates")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
